import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Truck, FileText, Wrench, Shield, Calendar, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const STATUS_COLORS = {
  active: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
  pending_approval: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  inactive: 'bg-zinc-600 text-muted-foreground',
  maintenance: 'bg-red-500/20 text-red-400 border-red-500/30',
  decommissioned: 'bg-zinc-700 text-muted-foreground',
};

const DOC_STATUS_COLORS = {
  approved: 'bg-emerald-500/20 text-emerald-400',
  pending: 'bg-amber-500/20 text-amber-400',
  expired: 'bg-red-500/20 text-red-400',
};

const INSPECTION_STATUS_COLORS = {
  pass: 'bg-emerald-500/20 text-emerald-400',
  fail: 'bg-red-500/20 text-red-400',
  conditional: 'bg-amber-500/20 text-amber-400',
};

const Row = ({ label, value }) => (
  <div className="flex justify-between items-center py-1">
    <span className="text-[10px] text-muted-foreground">{label}</span>
    <span className="text-xs text-foreground text-right">{value || '—'}</span>
  </div>
);

const formatDate = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-CA', { year: 'numeric', month: 'short', day: 'numeric' });
};

export default function AdminEquipmentDetail() {
  const { equipmentId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [equipment, setEquipment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [approving, setApproving] = useState(false);

  const fetchData = () => {
    if (!token || !equipmentId) return;
        api.get('/admin/fleet-overview')
      .then(res => {
        const all = res.data || [];
        const eq = all.find(e => e.equipment_id === equipmentId);
        if (eq) setEquipment(eq);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchData(); }, [token, equipmentId]);

  const handleApprove = async () => {
    setApproving(true);
    try {
      await api.put(`/equipment/${equipmentId}/approve`);
      toast.success('Equipment approved successfully');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to approve equipment');
    } finally {
      setApproving(false);
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <Loader2 className="w-6 h-6 text-muted-foreground animate-spin" />
    </div>
  );

  if (!equipment) return (
    <div className="text-center py-12">
      <Truck className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
      <p className="text-muted-foreground">Equipment not found</p>
      <Button variant="outline" onClick={() => navigate('/admin/equipment')} className="mt-4 border-input text-muted-foreground">
        Back to Equipment
      </Button>
    </div>
  );

  const documents = equipment.documents || [];
  const maintenanceHistory = equipment.maintenance || [];
  const inspections = equipment.inspections || [];
  const statusHistory = equipment.status_history || [];

  return (
    <div>
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Button variant="ghost" onClick={() => navigate('/admin/equipment')} className="text-muted-foreground hover:text-foreground p-2 h-auto">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex items-center gap-4 flex-1">
          <div className="w-16 h-16 rounded-full bg-brand-accent/20 flex items-center justify-center">
            <Truck className="w-8 h-8 text-brand-accent" />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">
              {equipment.make || 'Unknown'} {equipment.model || ''} {equipment.year || ''}
            </h1>
            <p className="text-sm text-muted-foreground">
              {equipment.equipment_type?.replace(/_/g, ' ')?.toUpperCase()}
              {equipment.unit_number ? ` · Unit #${equipment.unit_number}` : ''}
            </p>
          </div>
          <Badge className={`ml-4 text-xs ${STATUS_COLORS[equipment.status] || 'bg-zinc-700 text-muted-foreground'}`}>
            {equipment.status?.replace(/_/g, ' ').toUpperCase()}
          </Badge>
        </div>
      </div>

      {/* 3 Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

        {/* Column 1: Equipment Info */}
        <Card className="bg-card border-border">
          <CardContent className="p-4 space-y-4">
            <p className="text-[10px] text-muted-foreground tracking-wider font-bold">EQUIPMENT DETAILS</p>
            <Row label="VIN" value={equipment.vin} />
            <Row label="License Plate" value={
              equipment.license_plate
                ? `${equipment.license_plate}${equipment.license_plate_province ? ` (${equipment.license_plate_province})` : ''}`
                : null
            } />
            <Row label="Unit #" value={equipment.unit_number} />
            <Row label="Odometer" value={equipment.current_odometer_km ? `${Number(equipment.current_odometer_km).toLocaleString()} km` : null} />
            <Row label="Status" value={equipment.status?.replace(/_/g, ' ')} />

            {/* Owner Info */}
            <div className="border-t border-border pt-3">
              <p className="text-[10px] text-muted-foreground tracking-wider font-bold mb-2">OWNER</p>
              <Row label="Owner ID" value={equipment.owner_id} />
              {equipment.owner_name && <Row label="Name" value={equipment.owner_name} />}
              {equipment.owner_email && <Row label="Email" value={equipment.owner_email} />}
            </div>

            {/* Approval Info */}
            {equipment.approved_by && (
              <div className="border-t border-border pt-3">
                <p className="text-[10px] text-muted-foreground tracking-wider font-bold mb-2">APPROVAL</p>
                <Row label="Approved By" value={equipment.approved_by} />
                <Row label="Approved At" value={formatDate(equipment.approved_at)} />
              </div>
            )}

            {/* Approve Button */}
            {equipment.status === 'pending_approval' && (
              <div className="border-t border-border pt-3">
                <Button
                  className="w-full bg-emerald-600 hover:bg-emerald-700 text-foreground text-xs"
                  onClick={handleApprove}
                  disabled={approving}
                >
                  {approving ? <Loader2 className="w-3 h-3 mr-2 animate-spin" /> : <Shield className="w-3 h-3 mr-2" />}
                  Approve Equipment
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Column 2: Documents & Maintenance */}
        <div className="space-y-6">
          {/* Documents */}
          <Card className="bg-card border-border">
            <CardContent className="p-4 space-y-3">
              <div className="flex items-center gap-2">
                <FileText className="w-3.5 h-3.5 text-brand-accent" />
                <p className="text-[10px] text-muted-foreground tracking-wider font-bold">DOCUMENTS ({documents.length})</p>
              </div>
              {documents.length === 0 && (
                <p className="text-xs text-muted-foreground py-4 text-center">No documents on file</p>
              )}
              {documents.map((doc, i) => (
                <div key={doc.document_id || i} className="bg-muted/50 rounded-lg p-3">
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-xs text-foreground font-medium truncate">
                      {doc.document_type?.replace(/_/g, ' ') || 'Document'}
                    </p>
                    <Badge className={`text-[10px] ${DOC_STATUS_COLORS[doc.status] || 'bg-zinc-700 text-muted-foreground'}`}>
                      {doc.status?.toUpperCase() || 'UNKNOWN'}
                    </Badge>
                  </div>
                  {doc.file_name && <p className="text-[10px] text-muted-foreground truncate">{doc.file_name}</p>}
                  {doc.expiry_date && (
                    <div className="flex items-center gap-1 mt-1">
                      <Calendar className="w-2.5 h-2.5 text-muted-foreground" />
                      <p className="text-[10px] text-muted-foreground">Expires {formatDate(doc.expiry_date)}</p>
                    </div>
                  )}
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Maintenance History */}
          <Card className="bg-card border-border">
            <CardContent className="p-4 space-y-3">
              <div className="flex items-center gap-2">
                <Wrench className="w-3.5 h-3.5 text-brand-accent" />
                <p className="text-[10px] text-muted-foreground tracking-wider font-bold">MAINTENANCE HISTORY ({maintenanceHistory.length})</p>
              </div>
              {maintenanceHistory.length === 0 && (
                <p className="text-xs text-muted-foreground py-4 text-center">No maintenance records</p>
              )}
              {maintenanceHistory.map((m, i) => (
                <div key={m.maintenance_id || i} className="bg-muted/50 rounded-lg p-3">
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-xs text-foreground font-medium truncate">
                      {m.maintenance_type?.replace(/_/g, ' ') || m.type?.replace(/_/g, ' ') || 'Maintenance'}
                    </p>
                    <Badge className={`text-[10px] ${
                      m.status === 'completed' ? 'bg-emerald-500/20 text-emerald-400' :
                      m.status === 'scheduled' ? 'bg-amber-500/20 text-amber-400' :
                      m.status === 'overdue' ? 'bg-red-500/20 text-red-400' :
                      'bg-zinc-700 text-muted-foreground'
                    }`}>
                      {m.status?.toUpperCase() || 'N/A'}
                    </Badge>
                  </div>
                  {m.description && <p className="text-[10px] text-muted-foreground">{m.description}</p>}
                  <div className="flex items-center justify-between mt-1">
                    {m.cost != null && <p className="text-[10px] text-brand-accent">${Number(m.cost).toLocaleString()}</p>}
                    {(m.date || m.completed_at) && (
                      <p className="text-[10px] text-muted-foreground">{formatDate(m.date || m.completed_at)}</p>
                    )}
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>

        {/* Column 3: Inspections & History */}
        <div className="space-y-6">
          {/* Inspections */}
          <Card className="bg-card border-border">
            <CardContent className="p-4 space-y-3">
              <div className="flex items-center gap-2">
                <Shield className="w-3.5 h-3.5 text-brand-accent" />
                <p className="text-[10px] text-muted-foreground tracking-wider font-bold">INSPECTIONS ({inspections.length})</p>
              </div>
              {inspections.length === 0 && (
                <p className="text-xs text-muted-foreground py-4 text-center">No inspection records</p>
              )}
              {inspections.map((insp, i) => (
                <div key={insp.inspection_id || i} className="bg-muted/50 rounded-lg p-3">
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-xs text-foreground font-medium">
                      {insp.inspection_type?.replace(/_/g, ' ') || insp.type?.replace(/_/g, ' ') || 'Inspection'}
                    </p>
                    <Badge className={`text-[10px] ${
                      INSPECTION_STATUS_COLORS[insp.overall_status] ||
                      INSPECTION_STATUS_COLORS[insp.result] ||
                      'bg-zinc-700 text-muted-foreground'
                    }`}>
                      {(insp.overall_status || insp.result || 'N/A').toUpperCase()}
                    </Badge>
                  </div>
                  {(insp.date || insp.inspection_date) && (
                    <div className="flex items-center gap-1 mt-1">
                      <Calendar className="w-2.5 h-2.5 text-muted-foreground" />
                      <p className="text-[10px] text-muted-foreground">{formatDate(insp.date || insp.inspection_date)}</p>
                    </div>
                  )}
                  {insp.items_checked != null && (
                    <p className="text-[10px] text-muted-foreground mt-1">{insp.items_checked} items checked</p>
                  )}
                  {insp.notes && <p className="text-[10px] text-muted-foreground mt-1">{insp.notes}</p>}
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Status Change History */}
          {statusHistory.length > 0 && (
            <Card className="bg-card border-border">
              <CardContent className="p-4 space-y-3">
                <div className="flex items-center gap-2">
                  <Calendar className="w-3.5 h-3.5 text-brand-accent" />
                  <p className="text-[10px] text-muted-foreground tracking-wider font-bold">STATUS HISTORY ({statusHistory.length})</p>
                </div>
                {statusHistory.map((entry, i) => (
                  <div key={i} className="bg-muted/50 rounded-lg p-3 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Badge className={`text-[10px] ${STATUS_COLORS[entry.from_status] || 'bg-zinc-700 text-muted-foreground'}`}>
                        {entry.from_status?.replace(/_/g, ' ').toUpperCase() || '—'}
                      </Badge>
                      <span className="text-[10px] text-muted-foreground">→</span>
                      <Badge className={`text-[10px] ${STATUS_COLORS[entry.to_status] || 'bg-zinc-700 text-muted-foreground'}`}>
                        {entry.to_status?.replace(/_/g, ' ').toUpperCase() || '—'}
                      </Badge>
                    </div>
                    <p className="text-[10px] text-muted-foreground">{formatDate(entry.changed_at || entry.date)}</p>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
